import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { ROLE_LABELS, type CompanyRole } from '../lib/types';
import { formatDateTime } from '../lib/dates';

interface Invitation {
  id: string;
  company: { id: string; name: string; code: string };
  role: CompanyRole;
  invitedBy: string | null;
  invitedAt: string;
}

/**
 * What somebody has been asked to join, waiting for their answer.
 *
 * NOTHING HAPPENS UNTIL THEY ANSWER HERE. An invitation is an ask, not a grant: whoever sent
 * it sees "waiting for them" and learns nothing else, which is what stops a customer's lead
 * discovering whose addresses already have a Hub account by inviting them one at a time.
 *
 * Reached from the bell, from the email, or by typing it — all three land on the same page,
 * because the answer has to be given in a signed-in session either way. There is no token in
 * the link for the same reason: a forwarded email grants nobody anything.
 */
export function InvitationsPage() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [rows, setRows] = useState<Invitation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await api.get<Invitation[]>('/me/invitations'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your invitations');
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function answer(invitation: Invitation, accept: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/me/invitations/${invitation.id}/${accept ? 'accept' : 'decline'}`);

      /**
       * The session carries which companies somebody belongs to, so accepting has to refresh
       * it — otherwise the sidebar and every company picker would go on showing the world as
       * it was before they said yes.
       */
      await refresh();
      if (accept) {
        navigate(`/companies/${invitation.company.id}`);
        return;
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not answer that invitation');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Your account</div>
          <h1>Invitations</h1>
          <p className="subtitle">
            Companies you have been asked to join. Nothing is shared with you until you accept.
          </p>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      {rows === null ? (
        <div className="empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>Nothing waiting</h3>
            <p>You have no invitations at the moment.</p>
          </div>
        </div>
      ) : (
        <div className="stack-sm">
          {rows.map((invitation) => (
            <div key={invitation.id} className="card">
              <div className="card-pad row-between" style={{ gap: 12 }}>
                <span className="grow" style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{invitation.company.name}</div>
                  <div className="dim" style={{ fontSize: 12 }}>
                    {invitation.invitedBy ? `${invitation.invitedBy} invited you` : 'You were invited'} ·{' '}
                    {formatDateTime(invitation.invitedAt)} · as {ROLE_LABELS[invitation.role]}
                  </div>
                </span>

                <span className="row" style={{ gap: 7 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => void answer(invitation, true)}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    onClick={() => void answer(invitation, false)}
                  >
                    Decline
                  </button>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
